import { Capacitor } from "@capacitor/core";

/**
 * Push registration for the web (Service Worker + PushManager) and the
 * Capacitor shell (@capacitor/push-notifications). Both hand the result to
 * POST /api/me/push/subscribe.
 */
const base = import.meta.env.VITE_API_ORIGIN ?? "";

export type PushStatus = "unsupported" | "denied" | "default" | "granted";

export function pushStatus(): PushStatus {
  if (Capacitor.isNativePlatform()) return "default";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return Notification.permission as PushStatus;
}

async function post(path: string, body: unknown): Promise<void> {
  const res = await fetch(base + path, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`push subscribe failed (${res.status})`);
}

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export async function enablePush(): Promise<void> {
  if (Capacitor.isNativePlatform()) return enableNativePush();
  if (pushStatus() === "unsupported") throw new Error("このブラウザは通知に対応していません");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("通知が許可されませんでした");
  const keyRes = await fetch(`${base}/api/push/vapid-public-key`);
  const { key } = (await keyRes.json()) as { key: string | null };
  if (!key) throw new Error("サーバーに通知用の鍵が設定されていません");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) as BufferSource });
  await post("/api/me/push/subscribe", { platform: "web", subscription: sub.toJSON() });
}

async function enableNativePush(): Promise<void> {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") throw new Error("通知が許可されませんでした");
  const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
  await new Promise<void>((resolve, reject) => {
    void PushNotifications.addListener("registration", (token) => {
      post("/api/me/push/subscribe", { platform, token: token.value }).then(resolve, reject);
    });
    void PushNotifications.addListener("registrationError", (e) => reject(new Error(e.error)));
    void PushNotifications.register();
  });
}

export async function sendTestPush(): Promise<{ sent: number; failed: number; skipped: number }> {
  const res = await fetch(`${base}/api/me/push/test`, { method: "POST", credentials: "include" });
  if (!res.ok) throw new Error(`test push failed (${res.status})`);
  return (await res.json()) as { sent: number; failed: number; skipped: number };
}
